import type { AssetResponse, AssetsResponse, Eod, ParticipantResponse, PortfolioResponse, PositionResponse, RankingResponse, SessionResponse, Video } from '@zenit/contracts';
import type { OpName } from '@zenit/engine';
import { keepPreviousData, useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api, ApiError, clean, submitOrder } from './api';
import { AppLogic, initialState, type Actions, type Data, type UiState } from './logic/app-logic';
import { AppTemplate } from './prototype/app.template';
import { mergePages, unpriced } from './logic/catalog';
import { AUTH_PATH, afterSignIn, authRoute, signedInRedirect, type AuthRoute } from './auth/auth-logic';
import { AuthScreens, type AuthNav } from './auth/screens';
import { slots } from './dc/runtime';
import { useLang } from './i18n';
import { LangToggle } from './i18n/toggle';

// The language switch in the header, before the participant's name (desktop and mobile share that group).
slots['81'] = { at: 'start', node: () => <LangToggle compact={window.innerWidth < 760} /> }; // 760: the app's mobile layout

// The prototype switched screens in memory; here every screen also has a URL, so Back and links work.
// The screens themselves are unchanged.
const TAB_PATH: Record<string, (s: UiState) => string> = {
  cartera: () => '/cartera', clasif: () => '/clasificacion', videos: () => '/videos',
  ficha: s => '/clasificacion/' + encodeURIComponent(s.viewPid ?? ''),
  pos: s => '/posicion/' + encodeURIComponent(s.posSym ?? '') + (s.posSide === 'short' ? '?lado=corto' : ''),
};
export function fromPath(path: string, search = ''): Partial<UiState> & { unknownPath?: true } {
  const m = path.match(/^\/(clasificacion|posicion)\/(.+)$/);
  if (m?.[1] === 'clasificacion') return { tab: 'ficha', viewPid: decodeURIComponent(m[2]) };
  if (m?.[1] === 'posicion') return { tab: 'pos', posSym: decodeURIComponent(m[2]), posSide: new URLSearchParams(search).get('lado') === 'corto' ? 'short' : null };
  if (path === '/clasificacion') return { tab: 'clasif' };
  if (path === '/videos') return { tab: 'videos' };
  return path === '/' || path === '/cartera' ? { tab: 'cartera' } : { tab: 'cartera', unknownPath: true };
}

/** What the API has answered, as the current screen may use it: never another participant's, symbol's or search's data. */
export function screenData(st: UiState, r: Partial<Data>): Data {
  const d = st.drawer, posSym = st.tab === 'pos' ? st.posSym : d?.kind === 'editPos' ? d.sym : undefined;
  const searching = d?.kind === 'add' && d.step === 'form' && !d.sym;
  return {
    session: r.session, sessionAt: r.sessionAt, portfolio: r.portfolio,
    ranking: st.tab === 'clasif' ? r.ranking : undefined,
    participant: st.tab === 'ficha' && r.participant?.pid === (st.viewPid ?? r.session?.participant.id) ? r.participant : undefined,
    position: posSym && r.position?.asset.sym === posSym ? r.position : undefined, videos: st.tab === 'videos' ? r.videos : undefined,
    assets: searching ? r.assets : undefined, asset: d?.sym && r.asset?.asset.sym === d.sym ? r.asset : undefined,
  };
}

/** What a failed request means for the app: the session ended (401), or a position/participant that is not there (404). */
export function queryErrorAction(e: unknown, key: readonly unknown[]): 'signedOut' | 'admin' | 'notFound' | 'busy' | null {
  if (!(e instanceof ApiError)) return null;
  // A catalog listing the API could not price this minute (no credits free): back to the list, which asks again.
  if (e.status === 503 && e.error === 'busy' && key[0] === 'asset') return 'busy';
  if (e.status === 401) return 'signedOut';
  // An admin's session has no league (403): its page is Administración (QA 26 Oct 2026: it stayed on Bienvenida).
  if (e.status === 403) return 'admin';
  if (e.status === 404 && (key[0] === 'position' || key[0] === 'participant')) return 'notFound';
  return null;
}

const quietly = (f: () => Promise<void>) => { f().catch(e => console.warn('request failed:', e)); };

export function App() {
  useLang(); // every text re-renders in the chosen language
  const qc = useQueryClient();
  // The league's own login screens (Acceso, Crear cuenta…) have their own paths; every other path is the prototype's app.
  const [auth, setAuth] = useState<AuthRoute | null>(() => authRoute(location.pathname, location.search));
  const [st, setSt] = useState<UiState>(() => {
    if (authRoute(location.pathname, location.search)) return initialState(window.innerWidth);
    const { unknownPath, ...route } = fromPath(location.pathname, location.search);
    if (unknownPath) history.replaceState(null, '', '/cartera'); // the address bar names the screen shown
    return { ...initialState(window.innerWidth), ...route };
  });
  const stRef = useRef(st); stRef.current = st;
  const setState = useCallback((p: Partial<UiState> | ((s: UiState) => Partial<UiState>)) => setSt(s => ({ ...s, ...(typeof p === 'function' ? p(s) : p) })), []);

  const session = useQuery({ queryKey: ['session'], queryFn: () => api.session<SessionResponse>() });
  const S = session.data, inApp = !!S && st.scr === 'app', d = st.drawer;
  useEffect(() => { if (session.isSuccess) setState({ scr: S ? (S.participant.seen ? 'app' : 'first') : 'welcome' }); }, [session.isSuccess, S?.participant.id, S?.participant.seen]); // eslint-disable-line react-hooks/exhaustive-deps
  // Signed in, an address meant for signing in (or the bare /) shows Mi cartera and says so (QA 26 Oct 2026).
  useEffect(() => {
    const to = session.isSuccess && S ? signedInRedirect(location.pathname) : null;
    if (to) { setAuth(null); setState({ tab: 'cartera', drawer: null }); history.replaceState(null, '', to); }
  }, [session.isSuccess, S?.participant.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const on = (cond: boolean) => ({ enabled: inApp && cond, placeholderData: keepPreviousData });
  const portfolio = useQuery({ queryKey: ['portfolio'], queryFn: () => api.get<PortfolioResponse>('/portfolio'), ...on(true) });
  const ranking = useQuery({ queryKey: ['ranking'], queryFn: () => api.get<RankingResponse>('/ranking'), ...on(st.tab === 'clasif') });
  const participant = useQuery({ queryKey: ['participant', st.viewPid], queryFn: () => api.get<ParticipantResponse>('/participants/' + encodeURIComponent(st.viewPid ?? S!.participant.id)), ...on(st.tab === 'ficha') });
  const posSym = st.tab === 'pos' ? st.posSym : d?.kind === 'editPos' ? d.sym : undefined;
  const position = useQuery({ queryKey: ['position', posSym], queryFn: () => api.get<PositionResponse>('/positions/' + encodeURIComponent(posSym!)), ...on(!!posSym) });
  const videos = useQuery({ queryKey: ['videos'], queryFn: () => api.get<Video[]>('/videos'), ...on(st.tab === 'videos') });
  const searching = d?.kind === 'add' && d.step === 'form' && !d.sym;
  // The drawer's list: the featured assets, then the whole catalog, a page at a time as it scrolls (22 Oct 2026).
  const assetsQuery = (pillar: string, q: string, mkt: string) => ({
    queryKey: ['assets', pillar, q, mkt],
    queryFn: ({ pageParam }: { pageParam: string | null }) => api.get<AssetsResponse>('/assets?' + new URLSearchParams({ pillar, q, mkt, ...(pageParam ? { cursor: pageParam } : {}) })),
    initialPageParam: null as string | null, getNextPageParam: (last: AssetsResponse) => last.next ?? undefined,
  });
  const assets = useInfiniteQuery({ ...assetsQuery(d?.pillar, st.q, st.mkt), ...on(!!searching) });
  // The prototype's search step opens with its list in the same frame; preloading the lists while the pillar is chosen does the same.
  useEffect(() => { if (inApp && d?.kind === 'add' && d.step === 'pillar') for (const p of ['Bolsa', 'Criptomonedas', 'Materias primas']) void qc.prefetchInfiniteQuery(assetsQuery(p, '', '')); }, [inApp, d?.kind, d?.step]); // eslint-disable-line react-hooks/exhaustive-deps
  // Prices for the rows without one, one request at a time, each built from the rows on screen down (user, 23 Oct 2026:
  // overlapping requests sent before a scroll spent the minute's credits on rows no longer in view). The API prices
  // what its free credits allow; while some come back unpriced, the next request waits 10 s; listings without data drop out.
  const [prices, setPrices] = useState<Record<string, Eod | null>>({}), [gone, setGone] = useState<Set<string>>(() => new Set());
  const inflight = useRef(false), [firstRow, setFirstRow] = useState(0), [tick, setTick] = useState(0);
  const listed = mergePages(assets.data?.pages ?? [], prices, gone);
  useEffect(() => {
    if (!searching || inflight.current) return;
    const syms = unpriced(listed, new Set(), firstRow);
    if (!syms.length) return;
    inflight.current = true;
    const next = (ms: number) => setTimeout(() => { inflight.current = false; setTick(t => t + 1); }, ms);
    api.prices(syms).then(r => {
      setPrices(p => ({ ...p, ...r.prices }));
      if (r.unavailable.length) setGone(g => new Set([...g, ...r.unavailable]));
      next(syms.some(s => r.prices[s] === null) ? 10_000 : 0);
    }, () => next(10_000));
  }, [searching, listed?.results.length, prices, firstRow, tick]); // eslint-disable-line react-hooks/exhaustive-deps
  // The next page when the drawer's list is scrolled to within about 3 rows of its end.
  useEffect(() => {
    if (!searching) return;
    const onScroll = (e: Event) => {
      const t = e.target as HTMLElement;
      if (!(t instanceof HTMLElement) || !t.closest?.('aside[role="dialog"]') && t.getAttribute?.('role') !== 'dialog') return;
      // The first row on screen: rows are priced from there (user, 23 Oct 2026).
      const top = t.getBoundingClientRect().top, rows = [...t.querySelectorAll('[role="option"]')];
      const i = rows.findIndex(r => r.getBoundingClientRect().bottom > top);
      if (i >= 0) setFirstRow(i);
      if (t.scrollTop + t.clientHeight >= t.scrollHeight - 300 && assets.hasNextPage && !assets.isFetchingNextPage) void assets.fetchNextPage();
    };
    document.addEventListener('scroll', onScroll, true);
    return () => document.removeEventListener('scroll', onScroll, true);
  }, [searching, assets.hasNextPage, assets.isFetchingNextPage]); // eslint-disable-line react-hooks/exhaustive-deps
  const asset = useQuery({ queryKey: ['asset', d?.sym], queryFn: () => api.get<AssetResponse>('/assets/' + encodeURIComponent(d.sym)), ...on(!!d?.sym && d.kind !== 'editPos') });

  const D = screenData(st, { session: S ?? undefined, sessionAt: session.dataUpdatedAt, portfolio: portfolio.data, ranking: ranking.data,
    participant: participant.data, position: position.data, videos: videos.data, assets: listed, asset: asset.data });

  // ---- the prototype's side effects, as API calls
  const refresh = () => qc.invalidateQueries();
  const navigate = useCallback((tab: string, extra: Partial<UiState> = {}) => {
    const next = { ...stRef.current, ...extra, tab } as UiState;
    setState({ ...extra, tab });
    const url = TAB_PATH[tab](next);
    if (location.pathname + location.search !== url) history.pushState(null, '', url);
    window.scrollTo(0, 0);
  }, [setState]);
  const io = useRef<IntersectionObserver | null>(null);
  // The prototype's own-row observer: hide the pinned row while the real one is visible.
  // One function for the app's lifetime, as in the prototype: a new ref each render would detach and reattach it every time.
  // When the row moves (search, sort: rows are keyed by position) React detaches the old element, then attaches the new one.
  // The prototype's deferred "row gone" then raced the new observer's first answer; here it applies only if no row was
  // attached meanwhile, so the outcome is always the prototype's intended one: pinned exactly when the row is not in view.
  const ownRef = useCallback((el: Element | null) => {
    if (io.current) { io.current.disconnect(); io.current = null; }
    if (el) { io.current = new IntersectionObserver(([en]) => { if (en.isIntersecting !== stRef.current.ownVis) setState({ ownVis: en.isIntersecting }); }, { rootMargin: '-68px 0px -70px 0px' }); io.current.observe(el); }
    else if (stRef.current.ownVis) setTimeout(() => { if (!io.current) setState({ ownVis: false }); }, 0);
  }, [setState]);
  const nav: AuthNav = useMemo(() => ({
    go: (r, path) => {
      setAuth(r); const url = r ? AUTH_PATH[r.screen] : path ?? '/';
      if (location.pathname !== url) history.pushState(null, '', url);
      if (!r) setState({ ...fromPath(url), scr: 'welcome', drawer: null });
      window.scrollTo(0, 0);
    },
    signedIn: role => {
      const url = afterSignIn(role);
      if (role === 'admin') { setAuth({ screen: 'admin' }); history.replaceState(null, '', url); return; }
      // The page's first session check may still be out, asked before this cookie existed: invalidating alone would reuse
      // it (a first load is never cancelled for a refetch) and land on Bienvenida (CI, 26 Oct 2026).
      quietly(async () => { await qc.cancelQueries({ queryKey: ['session'] }); await qc.invalidateQueries({ queryKey: ['session'] }); setAuth(null); setState({ tab: 'cartera', drawer: null }); history.replaceState(null, '', url); window.scrollTo(0, 0); });
    },
    signedOut: () => { qc.clear(); setAuth(null); setState({ scr: 'welcome', drawer: null }); history.pushState(null, '', '/'); },
  }), [qc, setState]);
  const actions: Actions = {
    setState,
    // P01's "Entrar" opens Acceso (the prototype went straight into its demo).
    enter: () => nav.go({ screen: 'acceso' }),
    start: () => quietly(async () => { await api.seen(); await qc.invalidateQueries({ queryKey: ['session'] }); setState({ scr: 'app' }); }),
    logout: () => quietly(async () => { await api.logout(); nav.signedOut(); }),
    navigate,
    cancel: async oid => { await api.cancel(oid).catch(() => undefined); await refresh(); },
    changePillar: async (sym, pillar) => { await api.pillar(sym, pillar).catch(() => undefined); await refresh(); },
    place: (sym: string, op: OpName, body, done) => submitOrder(() => api.place(clean({ sym, op, ...body })), refresh, done),
    edit: (oid, body, done) => submitOrder(() => api.edit(oid, clean(body)), refresh, done),
    ownRef,
  };

  // Keyboard use of the drawer (QA 26 Oct 2026; the prototype did none of this): opening it moves the focus into it,
  // Escape closes it, and closing it gives the focus back to what opened it.
  const opener = useRef<HTMLElement | null>(null), open = !!st.drawer;
  useEffect(() => {
    if (open) {
      opener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      const dlg = document.querySelector<HTMLElement>('aside[role="dialog"]');
      if (dlg) { dlg.tabIndex = -1; dlg.style.outline = 'none'; dlg.focus({ preventScroll: true }); }
      const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setState({ drawer: null }); };
      window.addEventListener('keydown', esc);
      return () => window.removeEventListener('keydown', esc);
    }
    const el = opener.current; opener.current = null;
    if (el && el.isConnected) el.focus({ preventScroll: true });
  }, [open, setState]);

  // The prototype's 30-second tick (E.process, then a re-render): the API processes; the app refetches and re-renders.
  // Not under a fixed league clock, where nothing can change, as in the prototype's demo.
  useEffect(() => {
    if (!inApp || S?.fixedClock !== false) return;
    const iv = setInterval(() => { void qc.invalidateQueries(); }, 30_000);
    return () => clearInterval(iv);
  }, [inApp, S?.fixedClock, qc]);

  // A request that fails because the session ended goes back to Bienvenida; a position or participant that does not
  // exist (an old link) goes to Mi cartera.
  useEffect(() => qc.getQueryCache().subscribe(ev => {
    if (ev.type !== 'updated' || ev.action.type !== 'error') return;
    const what = queryErrorAction(ev.action.error, ev.query.queryKey);
    if (what === 'signedOut') { qc.setQueryData(['session'], null); setState({ drawer: null }); history.replaceState(null, '', '/'); }
    if (what === 'admin') { setAuth({ screen: 'admin' }); history.replaceState(null, '', '/admin'); }
    if (what === 'notFound') { setState({ tab: 'cartera', drawer: null }); history.replaceState(null, '', '/cartera'); }
    if (what === 'busy') setState(s => (s.drawer?.kind === 'add' ? { drawer: { ...s.drawer, sym: undefined, op: null } } : {}));
  }), [qc, setState]);

  useEffect(() => {
    const rs = () => setState({ w: window.innerWidth });
    const pop = () => { const a = authRoute(location.pathname, location.search); setAuth(a); if (!a) setState({ ...fromPath(location.pathname, location.search), drawer: null }); };
    window.addEventListener('resize', rs); window.addEventListener('popstate', pop);
    return () => { window.removeEventListener('resize', rs); window.removeEventListener('popstate', pop); io.current?.disconnect(); };
  }, [setState]);

  if (auth) return <><AuthScreens route={auth} nav={nav} /><LangToggle dark corner /></>;
  const vals = new AppLogic(st, D, actions).renderVals() as { isWelcome?: boolean };
  // Bienvenida has no header bar: the switch sits in its top right corner.
  return <>{AppTemplate(vals as never)}{vals.isWelcome ? <LangToggle corner /> : null}</>;
}
